import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});

function TermsPage() {
  useEffect(() => {
    document.title = "Terms of Service — Weborbis Sport";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Terms of Service for using weborbis.top and Weborbis Sport.");
    }
  }, []);
  return (
    <LegalPage title="Terms of Service" updated="July 21, 2026">
      <p>
        Welcome to Weborbis Sport, available at <strong>weborbis.top</strong> ("we", "us", "our",
        or the "Service"). These Terms of Service ("Terms") govern your access to and use of the
        Service. By accessing or using the Service, you agree to be bound by these Terms and our
        Privacy Policy. If you disagree with any part of these Terms, you may not access or use
        the Service.
      </p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        Your access to and use of the Service is conditioned on your acceptance of and compliance
        with these Terms. These Terms apply to all visitors, users, and others who access or use
        the Service.
      </p>

      <h2>2. User Rules and Responsibilities</h2>
      <p>
        Weborbis Sport provides editorial content, match reports, news summaries and tactical
        analysis related to international football. The content is provided for informational and
        entertainment purposes only and does not constitute professional advice of any kind.
      </p>
      <ul>
        <li>You must be at least 13 years old to use the Service.</li>
        <li>
          You agree not to use the Service in any way that violates applicable laws, rules, or
          regulations.
        </li>
        <li>You agree not to attempt to gain unauthorized access to any portion of the Service.</li>
        <li>
          You agree not to interfere with, disrupt, or place unreasonable load on our
          infrastructure.
        </li>
        <li>
          You agree not to reproduce, modify, distribute, or republish any content from the
          Service without our prior written consent, except for short excerpts with proper
          attribution.
        </li>
        <li>
          You are solely responsible for any activity that occurs through your use of the Service
          and for maintaining the confidentiality of any information associated with your use.
        </li>
      </ul>

      <h2>3. Intellectual Property</h2>
      <p>
        The Service and its original content, features, and functionality are and will remain the
        exclusive property of Weborbis Sport and our licensors. All original text, layouts,
        graphics, and site design published on weborbis.top are the property of Weborbis Sport
        unless otherwise stated.
      </p>
      <p>
        Third-party trademarks, logos, team crests and player names remain the property of their
        respective owners and are used only for editorial reference. Our trademarks and trade
        dress may not be used in connection with any product or service without our prior written
        consent.
      </p>

      <h2>4. User Content</h2>
      <p>
        If you post, link, or otherwise make available any content ("User Content") through the
        Service, you grant us a non-exclusive, worldwide, royalty-free license to use, modify,
        publicly perform, publicly display, reproduce, and distribute such User Content on and
        through the Service. You retain any and all of your rights to any User Content you submit.
      </p>
      <p>
        You are solely responsible for your User Content, including its legality, reliability, and
        appropriateness. You represent and warrant that you own or have the necessary licenses,
        rights, consents, and permissions to publish your User Content.
      </p>

      <h2>5. Third-Party Links</h2>
      <p>
        The Service may contain links to third-party websites or services that are not owned or
        controlled by Weborbis Sport. We have no control over, and assume no responsibility for,
        the content, privacy policies, or practices of any third-party websites or services. You
        acknowledge and agree that Weborbis Sport shall not be responsible or liable, directly or
        indirectly, for any damage or loss caused or alleged to be caused by or in connection with
        the use of or reliance on any such content, goods, or services available on or through any
        such websites or services.
      </p>

      <h2>6. Disclaimer of Warranties</h2>
      <p>
        YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. THE SERVICE IS PROVIDED ON AN "AS IS" AND
        "AS AVAILABLE" BASIS. THE SERVICE IS PROVIDED WITHOUT WARRANTIES OF ANY KIND, WHETHER
        EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF
        PERFORMANCE.
      </p>
      <p>
        We do not warrant that the Service will be uninterrupted, secure, or error-free. We strive
        to publish accurate and up-to-date information, but football events, scores, rosters and
        statistics change rapidly. We do not warrant that any information on the site is complete,
        accurate, or free from error.
      </p>

      <h2>7. Limitation of Liability</h2>
      <p>
        IN NO EVENT SHALL WEBORBIS SPORT, NOR ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS,
        SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR
        PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR
        OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR
        USE THE SERVICE.
      </p>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY LAW, WEBORBIS SPORT SHALL NOT BE LIABLE FOR ANY DAMAGES
        WHATSOEVER, WHETHER DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE,
        ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.
      </p>

      <h2>8. Indemnification</h2>
      <p>
        You agree to defend, indemnify and hold Weborbis Sport harmless from and against any and
        all claims, damages, obligations, losses, liabilities, costs or debt, and expenses
        (including but not limited to attorney's fees), resulting from or arising out of your use
        and access of the Service, or a breach of these Terms.
      </p>

      <h2>9. Account Termination and Suspension</h2>
      <p>
        We may terminate or suspend your access to the Service immediately, without prior notice
        or liability, for any reason whatsoever, including without limitation if you breach these
        Terms.
      </p>
      <p>
        If you wish to terminate your relationship with Weborbis Sport, you may simply stop using
        the Service. Upon termination, all provisions of these Terms which by their nature should
        survive termination shall survive termination, including, without limitation, ownership
        provisions, warranty disclaimers, indemnity, and limitations of liability.
      </p>

      <h2>10. Changes to These Terms</h2>
      <p>
        We reserve the right, at our sole discretion, to modify or replace these Terms at any time.
        We will try to provide at least 30 days' notice prior to any new terms taking effect. The
        updated Terms will be posted on this page with an updated "Last Updated" date.
      </p>
      <p>
        By continuing to access or use our Service after those revisions become effective, you
        agree to be bound by the revised terms. If you do not agree to the new terms, please stop
        using the Service.
      </p>

      <h2>11. Dispute Resolution</h2>
      <p>
        Any dispute arising out of or relating to these Terms or the Service shall first be
        submitted to good-faith negotiation. If the dispute cannot be resolved through
        negotiation, either party may pursue resolution through the courts as specified in Section
        12.
      </p>

      <h2>12. Governing Law and Jurisdiction</h2>
      <p>
        These Terms shall be governed and construed in accordance with the laws of the State of
        Delaware, United States, without regard to its conflict of law principles.
      </p>
      <p>
        You agree that any dispute arising out of or relating to these Terms or the Service shall
        be resolved exclusively in the state or federal courts located in New Castle County,
        Delaware, United States. You hereby consent to the personal jurisdiction of such courts
        and waive any objection to venue or inconvenience of forum.
      </p>

      <h2>13. Entire Agreement</h2>
      <p>
        These Terms constitute the entire agreement between Weborbis Sport and you regarding the
        Service and supersede and replace any prior agreements or understandings between us
        regarding the Service.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        If you have any questions about these Terms, please contact us at{" "}
        <strong>legal@weborbis.top</strong>.
      </p>
    </LegalPage>
  );
}
